import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { translate, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  AuthComponent,
  AuthService,
  BirdzTenantService,
  CardTenantComponent,
  DrawerComponent,
  HeaderComponent,
  HorizontalScrollDirective,
  IfAuthorizedDirective,
  LayoutComponent,
  LoaderService,
  MessengerService,
  ModalGdprComponent,
  Profile,
  SidebarComponent,
} from '@web/birdz-angular';
import { FilterComponent } from 'app/components/filter/filter.component';
import { ModalKpiSettingsComponent } from 'app/components/modal-kpi-settings/modal-kpi-settings.component';
import { Tab } from 'app/interfaces/tab.interface';
import { FilterService } from 'app/services/filter.service';
import { TabCacheService } from 'app/services/tab-cache.service';
import {
  catchError,
  combineLatest,
  filter,
  finalize,
  map,
  Observable,
  of,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { User } from '../../models/user.model';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    MatTabsModule,
    DrawerComponent,
    MatMenuModule,
    TranslocoPipe,
    RouterLink,
    RouterLinkActive,
    FilterComponent,
    LayoutComponent,
    HeaderComponent,
    SidebarComponent,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    AsyncPipe,
    AuthComponent,
    CardTenantComponent,
    IfAuthorizedDirective,
    HorizontalScrollDirective,
  ],
})
export class AppComponent implements OnInit {
  private userService = inject(UserService);
  private loader = inject(LoaderService);
  private auth = inject(AuthService);
  private messenger = inject(MessengerService);
  private title = inject(Title);
  dialog = inject(MatDialog);
  private transloco = inject(TranslocoService);
  private tenantService = inject(BirdzTenantService);
  private router = inject(Router);
  private tabCacheService = inject(TabCacheService);
  private filterService = inject(FilterService);

  profile$: Observable<Profile>;
  isLoading$: Observable<boolean>;
  settings$: Observable<any>;
  logoRedirectionUrl = '';
  needsTenant$: Observable<boolean>;
  techno$: Observable<string | null>;
  user$ = this.userService.user$;
  appIcon$ = this.userService.user$.pipe(map((user) => user?.getApplication('supervision')?.logo ?? ''));
  tabs: Tab[] = [
    {
      label: `TAB_DASHBOARD`,
      path: `dashboard`,
      icon: 'account_tree',
      technos: ['watermetering'],
    },
    {
      label: `TAB_KPI`,
      path: `kpis`,
      icon: 'analytics',
      technos: ['watermetering', 'sewerin', 'kapta'],
    },
  ];

  constructor() {
    this.profile$ = this.userService.user$.pipe(map((user) => user.toProfile()));
    this.settings$ = this.userService.user$.pipe(map((user: User) => user?.['settings']));
    this.isLoading$ = this.loader.isLoading$;
    this.needsTenant$ = combineLatest([
      this.userService.user$,
      this.tenantService.tenantId$.pipe(startWith(null)),
    ]).pipe(map(([user, tenantId]) => user?.isBirdzAdmin() === true && !tenantId));
    this.techno$ = this.filterService.filter$.pipe(map((filter) => filter.techno));

    this.initTabCaching();
  }

  ngOnInit() {
    this.setLogo();
    this.userService.fetchUser().subscribe();
    this.translateTitle();
    this.handleGDPR();
  }

  initTabCaching() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap((event) => {
          this.tabCacheService.storeChildrenTabCacheFromUrl(event.url);
        })
      )
      .subscribe();
  }

  handleGDPR() {
    this.userService.needConsent$
      .pipe(
        skipWhile((needConsent) => !needConsent),
        take(1)
      )
      .subscribe(() => {
        this.openGDPRModal(true);
      });
  }

  openKpiSettings() {
    this.dialog.open(ModalKpiSettingsComponent, {
      width: '600px',
      maxWidth: '95vw',
    });
  }

  openGDPRModal(requireConsent = false) {
    const config = {
      data: {
        appName$: this.transloco.selectTranslate('APP_NAME'),
        submit$: requireConsent ? this.userService.grantConsent() : null,
      },
    };

    this.dialog
      .open(ModalGdprComponent, config)
      .afterClosed()
      .subscribe(() => {
        if (requireConsent) {
          this.userService.setNeedConsent(false);
        }
      });
  }

  translateTitle() {
    this.transloco.langChanges$
      .pipe(
        switchMap(() => this.transloco.selectTranslate('APP_NAME')),
        tap((message) => this.title.setTitle(message))
      )
      .subscribe();
  }

  logout() {
    this.userService
      .clearCache() // clear cache first because this is an authenticated request
      .pipe(
        finalize(() =>
          this.auth
            .logout() // logout (clear cookie and logout from (keycloak) provider)
            .pipe(finalize(() => this.auth.redirectToLogin())) // redirect to login page
            .subscribe()
        )
      )
      .subscribe();
  }

  onUserSettingsChange(settings: Record<string, any>) {
    const langChanged = settings['lang'] !== this.transloco.getActiveLang();
    this.userService.patchSettings(settings).subscribe({
      next: () => {
        this.messenger.success(this.transloco.translate('USER_SETTINGS_UPDATED'));
        if (langChanged) {
          window.location.reload();
        }
      },
      error: (error) => this.messenger.error(error.friendlyMessage),
    });
  }

  contactCallback = (form: FormData) => {
    const extra = {};

    form.append('url', window.location.href);
    form.append('extra', JSON.stringify(extra));

    return this.userService.contactSupport(form).pipe(
      catchError((error) => {
        return throwError(error.friendlyMessage);
      })
    );
  };

  setLogo() {
    this.userService.user$.pipe(tap((user) => this.setLogoUrl(user))).subscribe();
  }

  setLogoUrl(user: User | null) {
    const portalApplication = user?.getApplication('portal');
    if (portalApplication) {
      this.logoRedirectionUrl = portalApplication.url;
    }
  }

  onAvatarFileChanged(file: File | null): void {
    if (!file) {
      return;
    }

    this.userService
      .updateUserAvatar(file)
      .pipe(
        tap((profile: Profile) => {
          this.profile$ = of(profile);
          this.messenger.success(translate('birdz.avatar_upload_success'));
        }),
        this.messenger.showFriendlyErrorMessage()
      )
      .subscribe();
  }
}
